import { CloudUpload, Launch, TrashCan } from '@carbon/icons-react';
import { Button, message, Modal, Upload } from 'antd';
import classNames from 'classnames';
import { GetFileContentsEntityTypes, showFilePreview } from 'components/FileList/FileList';
import { useMemo, useState } from 'react';
import { familyService, providerService } from 'services';

import './UploadFile.module.less';

export const UploadFileEntityTypes = {
  PROVIDER: 'provider',
  FAMILY: 'family',
};

export default function UploadFile({
  entityType = UploadFileEntityTypes.PROVIDER,
  fileCode,
  files,
  setFiles,
  orgId,
  applicationId,
  careFacilityId,
  readOnly,
  disabled,
  ariaLabel,
  className,
  buttonProps,
  preUploadButtonContent,
  uploadButtonText,
  preventDeleteIfLastFile = false,
  // Staging mode: skip upload, surface the raw File to the parent instead
  stagingMode = false,
  onStageFile,
  ...rest
}) {
  const [fileUploading, setFileUploading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [fileDeleting, setFileDeleting] = useState(false);
  const [stagedFiles, setStagedFiles] = useState([]);

  const uploadedFileList = useMemo(
    () =>
      files
        ?.filter((file) => file.fileType === fileCode)
        ?.map((file) => ({
          uid: file.id,
          name: file.name,
          status: 'done',
          url: file.name,
        })),
    [files, fileCode],
  );

  // In staging mode, show both already-uploaded files and the locally staged file
  const fileList = stagingMode ? [...(uploadedFileList || []), ...stagedFiles] : uploadedFileList;

  const handleBeforeUpload = async (file) => {
    if ((await checkIfFileTypeIsSupported(file)) === false) {
      handleUnsupportedFileType(file);
      return Upload.LIST_IGNORE;
    }

    if (checkIfFileSizeIsSupported(file) === false) {
      handleUnsupportedFileSize(file);
      return Upload.LIST_IGNORE;
    }

    if (stagingMode) {
      // Replace any previous staged file (one at a time)
      const staged = { uid: file.uid || String(Date.now()), name: file.name, status: 'done', originFileObj: file };
      setStagedFiles([staged]);
      onStageFile?.(file);
      return Upload.LIST_IGNORE;
    }
  };

  const handleRemove = (file) => {
    // Staged file — remove locally, no API call needed
    if (stagingMode && stagedFiles.some((sf) => sf.uid === file.uid)) {
      setStagedFiles([]);
      onStageFile?.(null);
      return false;
    }

    if (preventDeleteIfLastFile && fileList?.length === 1) {
      return false;
    }

    setFileToDelete(file);
    return false;
  };

  const handlePreview = (file) => {
    // Staged (local) file — open via object URL, no server needed
    if (file.originFileObj) {
      const url = URL.createObjectURL(file.originFileObj);
      window.open(url, '_blank');
      return;
    }

    let _previewEntityType;
    if (entityType === UploadFileEntityTypes.PROVIDER) {
      if (orgId) {
        _previewEntityType = GetFileContentsEntityTypes.PROVIDER_ORGANIZATION;
      } else if (applicationId) {
        _previewEntityType = GetFileContentsEntityTypes.PROVIDER_APPLICATION;
      } else if (careFacilityId) {
        _previewEntityType = GetFileContentsEntityTypes.PROVIDER_CARE_FACILITY;
      }
    } else {
      _previewEntityType = GetFileContentsEntityTypes.FAMILY;
    }

    showFilePreview({
      applicationId,
      careFacilityId,
      organizationId: orgId,
      file,
      entityType: _previewEntityType,
      setLoading: setPreviewLoading,
    });
  };

  return (
    <>
      <Upload
        className={classNames(
          'cc-uploadFile block',
          {
            readOnly: readOnly,
            hasFiles: fileList?.length !== 0,
          },
          className,
        )}
        {...{
          disabled: readOnly,
          accept: '.pdf, .jpg, .jpeg, .png, .heic',
          beforeUpload: handleBeforeUpload,
          customRequest: async ({ file, onSuccess, onError }) => {
            try {
              setFileUploading(true);

              let _uploadedFile;
              if (entityType === UploadFileEntityTypes.PROVIDER) {
                if (orgId) {
                  _uploadedFile = await providerService.uploadOrgFile(orgId, file, fileCode);
                } else if (applicationId) {
                  _uploadedFile = await providerService.uploadApplicationFile(applicationId, file, fileCode);
                } else if (careFacilityId) {
                  _uploadedFile = await providerService.uploadCareFacilityFile(careFacilityId, file, fileCode);
                }
              } else {
                if (applicationId) {
                  _uploadedFile = await familyService.uploadApplicationFile(applicationId, file, fileCode);
                } else {
                  message.error('Missing application id in UploadFile component');
                }
              }

              onSuccess(_uploadedFile);
              message.success('File uploaded');

              setFiles([...(files || []), _uploadedFile]);
            } catch (error) {
              onError(error);
              console.log('Error uploading file', error);
              message.error('Something went wrong');
            } finally {
              setFileUploading(false);
            }
          },
          fileList,
        }}
        onRemove={handleRemove}
        itemRender={(originNode, file, currentFileList, actions) => {
          const isStaged = stagedFiles.some((sf) => sf.uid === file.uid);

          return (
            <div className="ant-upload-list ant-upload-list-text">
              <div className="ant-upload-list-item-container">
                <div className="ant-upload-list-item ant-upload-list-item-done">
                  <div className="ant-upload-icon">
                    <Launch
                      style={{ marginTop: '7px', cursor: 'pointer', color: '#096DD9' }}
                      onClick={() => actions.preview()}
                      className="ant-upload-list-item-preview-icon"
                    />
                  </div>

                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ant-upload-list-item-name"
                    onClick={() => actions.preview()}
                  >
                    {file.name}
                  </a>

                  {isStaged && <span className="text-xs text-gray-400 mr-2 italic">pending upload</span>}

                  {!readOnly && !disabled && !(preventDeleteIfLastFile && currentFileList?.length === 1) && (
                    <a
                      className="px-4 underline hover:underline ant-upload-list-item-remove"
                      onClick={() => {
                        if (isStaged) {
                          setStagedFiles([]);
                          onStageFile?.(null);
                        } else {
                          setFileToDelete(file);
                        }
                      }}
                      aria-label="Remove file"
                      data-testid="remove-file"
                    >
                      <TrashCan className="align-middle mt-[-3px]" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        }}
        showUploadList={{
          showRemoveIcon: !readOnly && !disabled && !(preventDeleteIfLastFile && fileList?.length === 1),
          removeIcon: <a className="px-4 underline hover:underline">Remove</a>,
        }}
        data-testid={rest['data-testid'] || 'uploadFile'}
        onPreview={handlePreview}
      >
        <div className="flex items-center flex-wrap gap-4">
          {preUploadButtonContent}
          {!readOnly && (
            <Button
              icon={<CloudUpload />}
              loading={fileUploading}
              disabled={fileUploading || previewLoading || disabled}
              aria-label={ariaLabel}
              {...buttonProps}
            >
              {fileUploading ? 'Uploading...' : uploadButtonText || 'Upload file'}
            </Button>
          )}{' '}
          {preUploadButtonContent && <div className="w-full h-0" />}
          {fileList?.length === 0 && (
            <span
              className={classNames('text-black opacity-70', {
                'cursor-not-allowed': readOnly || disabled,
              })}
              onClick={(e) => {
                e.preventDefault?.();
                e.stopPropagation?.();
              }}
            >
              {readOnly || disabled ? 'No files uploaded' : 'Accepted file types: PDF, JPG, PNG. File size limit: 10MB'}
            </span>
          )}
        </div>
      </Upload>

      <Modal
        title="Delete File?"
        open={fileToDelete !== null}
        onCancel={() => !fileDeleting && setFileToDelete(null)}
        closable={!fileDeleting}
        maskClosable={!fileDeleting}
        footer={null}
      >
        <p>Are you sure you want to delete this file? This action cannot be undone.</p>
        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={() => setFileToDelete(null)} disabled={fileDeleting}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            loading={fileDeleting}
            onClick={async () => {
              try {
                setFileDeleting(true);
                if (entityType === UploadFileEntityTypes.PROVIDER) {
                  if (orgId) {
                    await providerService.deleteOrgFile(orgId, fileToDelete.uid);
                  } else if (applicationId) {
                    await providerService.deleteApplicationFile(applicationId, fileToDelete.uid);
                  } else if (careFacilityId) {
                    await providerService.deleteCareFacilityFile(careFacilityId, fileToDelete.uid);
                  }
                } else {
                  if (applicationId) {
                    await familyService.deleteApplicationFile(applicationId, fileToDelete.uid);
                  } else {
                    message.error('Missing application id in UploadFile component');
                  }
                }

                setFiles(files?.filter((f) => f.id !== fileToDelete.uid));
                message.success('File removed');
                setFileToDelete(null);
              } finally {
                setFileDeleting(false);
              }
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}

export const checkIfFileTypeIsSupported = async (file) => {
  const isAcceptedImage = ['image/jpg', 'image/jpeg', 'image/png', 'image/heic'].includes(file.type);
  const isPDF = ['application/pdf', 'application/octet-stream'].includes(file.type);

  if (isAcceptedImage || isPDF) {
    return true;
  }

  // HEIC - Extension first
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'heic') {
    return true;
  }

  // HEIC - Fallback: check file signature
  if (file.size > 12) {
    try {
      const buffer = await file.slice(0, 12).arrayBuffer();
      const view = new Uint8Array(buffer);
      const signature = String.fromCharCode(...view.slice(4, 8));

      return (
        signature === 'ftyp' &&
        (String.fromCharCode(...view.slice(8, 12)) === 'heic' || String.fromCharCode(...view.slice(8, 12)) === 'heix')
      );
    } catch (e) {
      console.warn('Could not read file header:', e);

      return false;
    }
  }

  return false;
};

export const handleUnsupportedFileType = (file) => {
  message.error(`${file.name} must be a jpg, png, or pdf file`);
};

export const checkIfFileSizeIsSupported = (file) => {
  const FILESIZE_LIMIT = 10 * 1024 * 1024; // 10MB
  return file.size <= FILESIZE_LIMIT;
};

export const handleUnsupportedFileSize = (file) => {
  message.error(`${file.name} must be smaller than 10MB`);
};
